import { getCurrentLanguage } from '@src/shared/lib/culture';
import type { Nullable } from '@src/shared/types/common';
import type { IBackPlacesServiceInformation } from '@legacy/scripts/types/modules/back-places-service';

import { applyOnKendoFiltering } from './apply-on-kendo-filtering';

type DadataSuggestion = {
    value: string;
    unrestricted_value: string;
    data: {
        geo_lat?: string;
        geo_lon?: string;
        country?: string;
        city_with_type?: string;
        settlement_with_type?: string;
        region_with_type?: string;
    };
};

const DADATA_SUGGEST_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address';
const DADATA_GEOLOCATE_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/geolocate/address';

/**
 * Сервис для работы с API Dadata (аналог BackPlacesService, но через Dadata вместо Google Places).
 */
export class BackDadataService {
    static isInitialization = false;
    static token: string;

    static get headers(): Record<string, string> {
        return {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: 'Token ' + BackDadataService.token
        };
    }

    static init(): void {
        if (!BackDadataService.isInitialization) {
            BackDadataService.isInitialization = true;
            BackDadataService.token = window.DadataToken ?? '';
        }
    }

    /** Язык запросов к Dadata: русский — только для русской локали, иначе английский */
    private static getLanguage(): 'ru' | 'en' {
        return getCurrentLanguage() === 'ru' ? 'ru' : 'en';
    }

    // Автокомплит (Kendo UI)
    static async onKendoFiltering(e: kendo.ui.ComboBoxFilteringEvent) {
        BackDadataService.init();
        await applyOnKendoFiltering(e, (input) => BackDadataService.getQueryPredictions(input));
    }

    // Подсказки (suggest/address)
    static async getQueryPredictions(input: string): Promise<Array<{ text: string; value: string }>> {
        const response = await fetch(DADATA_SUGGEST_URL, {
            method: 'POST',
            mode: 'cors',
            headers: BackDadataService.headers,
            body: JSON.stringify({
                query: input,
                count: 10,
                language: BackDadataService.getLanguage()
            })
        });

        if (!response.ok) {
            console.warn('[BackDadataService] Ошибка запроса suggest/address:', response.status);
            return [];
        }

        const data = await response.json();

        if (!data?.suggestions?.length) {
            return [];
        }

        return data.suggestions.map(
            (s: { value: string; unrestricted_value: string; data: Record<string, unknown> }) => ({
                text: s.value,
                value: s.unrestricted_value
            })
        );
    }

    // Геокодирование: адрес - информация
    static getObjectByAddress(
        address: string,
        callback: (info: Nullable<IBackPlacesServiceInformation>) => void
    ) {
        BackDadataService.init();

        fetch(DADATA_SUGGEST_URL, {
            method: 'POST',
            mode: 'cors',
            headers: BackDadataService.headers,
            body: JSON.stringify({
                query: address,
                count: 1,
                language: BackDadataService.getLanguage()
            })
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                callback(BackDadataService.getObjInformation(data?.suggestions));
            })
            .catch((error: unknown) => {
                console.warn('[BackDadataService] Ошибка getObjectByAddress:', error);
                callback(null);
            });
    }

    // Геокодирование: координаты - адрес
    static getObjectByCoordinates(
        [lat, lng]: number[],
        callback: (info: Nullable<IBackPlacesServiceInformation>) => void,
        language: 'ru' | 'en' = BackDadataService.getLanguage()
    ) {
        BackDadataService.init();

        fetch(DADATA_GEOLOCATE_URL, {
            method: 'POST',
            mode: 'cors',
            headers: BackDadataService.headers,
            body: JSON.stringify({ lat, lon: lng, count: 1, radius_meters: 1000, language })
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return response.json();
            })
            .then((data) => callback(BackDadataService.getObjInformation(data?.suggestions)))
            .catch((error: unknown) => {
                console.warn('[BackDadataService] Ошибка geolocate/address:', error);
                callback(null);
            });
    }

    // Парсинг ответа Dadata
    static getObjInformation(suggestions: Nullable<DadataSuggestion[]>): Nullable<IBackPlacesServiceInformation> {
        if (!suggestions?.length) {
            return null;
        }

        const { unrestricted_value, data } = suggestions[0];
        const geoLat = Number(data.geo_lat) || null;
        const geoLon = Number(data.geo_lon) || null;

        return {
            fullAddress: unrestricted_value,
            city: data.city_with_type || data.settlement_with_type || '',
            region: data.region_with_type || '',
            country: data.country || '',
            coords: geoLat != null && geoLon != null ? [geoLat, geoLon] : []
        };
    }
}

window.BackDadataService = BackDadataService;
